import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataState,
  Input,
  Modal,
  PageHeader,
  Select,
  Table,
  useToast,
  type Column,
} from "@vms/ui-web";
import { useApi, useCanIDo } from "../../auth/AuthProvider";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";

const COMMON_SCOPES = [
  "*",
  "booking.*",
  "booking.created",
  "booking.confirmed",
  "booking.cancelled",
  "session.*",
  "session.completed",
  "lap.recorded",
];

interface SubscriptionRow {
  id: string;
  name: string;
  targetUrl: string;
  scopes: string[];
  adapter: string | null;
  isActive: boolean;
  createdAt: string;
}

export function Webhooks() {
  const { currentOrg } = useOrgContext();
  const api = useApi();
  const canIDo = useCanIDo();
  const { addToast } = useToast();

  const subs = useQuery<SubscriptionRow[]>(
    currentOrg ? `/api/organisations/${currentOrg.id}/webhook-subscriptions` : null,
  );
  const [creating, setCreating] = useState(false);
  const [reveal, setReveal] = useState<{ name: string; secret: string } | null>(null);
  const [toggling, setToggling] = useState<SubscriptionRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (!currentOrg) return null;
  const canManage = canIDo("partners:manage", currentOrg.id);

  async function setActive(sub: SubscriptionRow, isActive: boolean) {
    setBusyId(sub.id);
    try {
      const res = await api(
        `/api/organisations/${currentOrg!.id}/webhook-subscriptions/${sub.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ isActive }),
        },
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to update", "error");
        return;
      }
      addToast(`${sub.name} ${isActive ? "enabled" : "disabled"}`);
      subs.refetch();
    } finally {
      setBusyId(null);
      setToggling(null);
    }
  }

  const columns: Column<SubscriptionRow>[] = [
    {
      key: "name",
      header: "Name",
      render: (s) => (
        <div>
          <div className="font-medium text-gray-900">{s.name}</div>
          <div className="text-xs text-gray-500 truncate max-w-md">{s.targetUrl}</div>
        </div>
      ),
    },
    {
      key: "scopes",
      header: "Events",
      render: (s) => (
        <div className="flex flex-wrap gap-1 max-w-xs">
          {s.scopes.map((sc) => (
            <Badge key={sc} className="bg-gray-100 text-gray-700">
              <code className="text-[10px]">{sc}</code>
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: "adapter",
      header: "Adapter",
      render: (s) => (
        <span className="text-xs text-gray-500">{s.adapter ?? "canonical"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (s) =>
        s.isActive ? (
          <Badge className="bg-green-100 text-green-700">Active</Badge>
        ) : (
          <Badge className="bg-gray-100 text-gray-700">Disabled</Badge>
        ),
    },
    {
      key: "actions",
      header: "",
      render: (s) =>
        canManage ? (
          <div className="text-right">
            <Button
              size="sm"
              variant={s.isActive ? "secondary" : "primary"}
              loading={busyId === s.id}
              onClick={() => (s.isActive ? setToggling(s) : setActive(s, true))}
            >
              {s.isActive ? "Disable" : "Enable"}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Webhook subscriptions"
        subtitle="Outbound integrations — Slack, partner endpoints, custom listeners. Signed with HMAC-SHA256."
      >
        {canManage && <Button onClick={() => setCreating(true)}>New subscription</Button>}
      </PageHeader>

      <Card className="overflow-hidden">
        <DataState
          loading={subs.loading}
          error={subs.error}
          empty={!subs.loading && (subs.data ?? []).length === 0}
          emptyMessage="No webhook subscriptions yet."
        >
          <Table<SubscriptionRow>
            columns={columns}
            data={subs.data ?? []}
            keyExtractor={(s) => s.id}
          />
        </DataState>
      </Card>

      <NewSubscriptionModal
        open={creating}
        onClose={() => setCreating(false)}
        orgId={currentOrg.id}
        onCreated={(name, secret) => {
          subs.refetch();
          setReveal({ name, secret });
        }}
      />

      <Modal
        isOpen={!!reveal}
        onClose={() => setReveal(null)}
        title="Signing secret — copy now"
      >
        {reveal && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              This is the only time you'll see <span className="font-medium">{reveal.name}</span>'s
              signing secret. Configure it on the receiving end to verify the
              <code className="text-xs px-1 mx-0.5 bg-gray-100 rounded">VMS-Signature</code>
              header on each delivery.
            </div>
            <pre className="rounded bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto select-all">
              {reveal.secret}
            </pre>
            <div className="flex justify-end">
              <Button onClick={() => setReveal(null)}>Done</Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!toggling}
        title="Disable subscription?"
        message={
          toggling
            ? `Stop sending events to ${toggling.name}? You can re-enable it any time. In-flight deliveries continue.`
            : ""
        }
        confirmLabel="Disable"
        loading={!!toggling && busyId === toggling.id}
        onClose={() => setToggling(null)}
        onConfirm={() => toggling && setActive(toggling, false)}
      />
    </div>
  );
}

function NewSubscriptionModal({
  open,
  onClose,
  orgId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onCreated: (name: string, secret: string) => void;
}) {
  const api = useApi();
  const { addToast } = useToast();
  const [name, setName] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [adapter, setAdapter] = useState<"" | "slack">("");
  const [scopes, setScopes] = useState<Set<string>>(new Set(["booking.confirmed"]));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName("");
    setTargetUrl("");
    setAdapter("");
    setScopes(new Set(["booking.confirmed"]));
    setError(null);
  }

  function toggleScope(s: string) {
    const next = new Set(scopes);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setScopes(next);
  }

  async function submit() {
    if (!name.trim() || !targetUrl.trim() || scopes.size === 0) {
      setError("Name, target URL and at least one event are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await api(`/api/organisations/${orgId}/webhook-subscriptions`, {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          targetUrl: targetUrl.trim(),
          scopes: Array.from(scopes),
          adapter: adapter || null,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.error ?? "Failed to create subscription");
        return;
      }
      addToast("Webhook subscription created");
      onCreated(json.data.name, json.data.secret);
      reset();
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen={open} onClose={() => { reset(); onClose(); }} title="New webhook subscription">
      <div className="space-y-4">
        <Input
          label="Name"
          placeholder="Slack #ops"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <Input
          label="Target URL"
          type="url"
          placeholder="https://hooks.slack.com/services/…"
          value={targetUrl}
          onChange={(e) => setTargetUrl(e.target.value)}
        />
        <Select
          label="Adapter"
          value={adapter}
          onChange={(e) => setAdapter(e.target.value as "" | "slack")}
          options={[
            { value: "", label: "Canonical JSON" },
            { value: "slack", label: "Slack-formatted message" },
          ]}
        />
        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">Events</div>
          <div className="grid grid-cols-2 gap-2">
            {COMMON_SCOPES.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={scopes.has(s)}
                  onChange={() => toggleScope(s)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <code className="text-xs">{s}</code>
              </label>
            ))}
          </div>
        </div>
        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={() => { reset(); onClose(); }}>
            Cancel
          </Button>
          <Button onClick={submit} loading={submitting}>
            Create &amp; reveal secret
          </Button>
        </div>
      </div>
    </Modal>
  );
}
